import { adminResourcesLanguageFrench } from '../resources/adminResources';

import type { CallflowTranslationMessages } from './callflowTranslationMessages';

const matchExitCauses = {
  continue_on_max_waiters: 'Continuer avec le nombre maximal d’attente',
  skip_empty: 'Ignorer vide',
  answered: 'Répondu',
  exit_key: 'Touche de sortie',
  hangup_on_max_waiters: 'Raccrocher au nombre maximal d’attente',
  timeout: 'Délai d’expiration',
  ...adminResourcesLanguageFrench.call_sessions.enums.hangup_cause,
};

export const callflowLanguageFrench: CallflowTranslationMessages = {
  callflows: {
    interface: {
      toggleListVisibility: 'Basculer la visibilité de la liste',
      toggleMiniMap: 'Basculer la mini-mapp',
      noRecordSelected: 'Aucun enregistrement sélectionné',
      selectRecord: "Sélectionner l'enregistrement",
      selectedRecord: 'Enregistrement sélectionné',
      insertElement: 'Insérer un élément',
      rules: 'Règles',
      services: 'Services',
      destinations: 'Destinations',
      selectionModeEnabled: 'Mode de sélection activé',
      panningModeEnabled: 'Mode panoramique activé',
      copyNode: 'Copier le noeud',
      deleteSelected: 'Supprimer la sélection',
      editAndSave: 'Modifier et enregistrer',
      editAndSaveDescription: 'Modifier et enregistrer les paramètres du dialplan.',
      description: 'Description',
      backToMainList: 'Retour à la liste principale',
      showBackups: 'Afficher les sauvegardes',
      backupsForTitle: 'Sauvegardes pour %{title}',
      backupData: 'Sauvegarde des données',
      restoreBackup: 'Restaurer la sauvegarde',
      saveAsFile: 'Enregistrer sous',
      uploadFile: 'Télécharger le fichier',
      importExport: 'Importer/Exporter',
      importExportDescription: 'Utilisez cette interface pour exporter/importer votre plan de numérotation.',
      controlsTitle: '%{node} contrôles',
      zoomIn: 'Zoom avant',
      zoomOut: 'Zoom arrière',
      fitView: 'Ajuster la vue',
      autoPositionNodes: 'Positionner automatiquement les noeuds',
      toggleInteractivity: 'Basculer l interactivité',
      unsavedExit: 'Vous avez des modifications non enregistrées. Voulez-vous quitter ?',
      filters: {
        backupFilter: 'Filtrer les sauvegardes',
        showAll: 'Tout afficher',
        showOnlyBackups: 'Afficher uniquement les sauvegardes',
        hideBackups: 'Masquer les sauvegardes',
      },
      draft: {
        dialogTitle: 'Il y a des modifications non enregitrées',
        dialogText:
          'Vous avez des modifications non sauvegardées qui sont plus récentes que la dernière version du callflow. Voulez-vous les appliquer?',
        decline: 'Annuler',
        apply: 'Appliquer',
      },
      soundfileUpload: {
        toggle: 'Charger un fichier audio',
        uploading: 'Chargement…',
      },
    },
    components: {
      callPropertyInput: {
        options: {
          called_id_number: 'Numéro ID Appelé',
          called_id_name: 'Nom ID Appelé',
          caller_id_number: 'Numéro ID Appelant',
          caller_id_name: 'Nom ID Appelant',
          remote_network_addr: 'Adresse Réseau Distant',
        },
      },
      matchReplaceTestInput: {
        testSubmit: 'Test',
        testMatchLabel: 'Tester les règles de correspondance',
        testMatchReplaceLabel: 'Tester la correspondance/le remplacement',
        replacedWith: 'Remplacé par %{replacement}',
        testError: 'Erreur de validation : %{error}',
      },
      sortableInstructionsList: {
        instructions: 'Instructions',
        description: 'Description',
        matchExitCauses: 'Cause de sortie correspondante',
        defaultConnection: 'Connexion par défaut',
        presetsLabel: 'Présets',
        presets: {
          busy: 'En conflit',
        },
        match_exit_causes: matchExitCauses,
      },
      variableInterpolation: {
        title: 'Interpolation de variables',
        description:
          "Vous pouvez utiliser des variables du contexte d'appel dans les URLs et les valeurs d'en-tête. Attention : référencer un namespace inconnu, une variable non définie (sans orempty), ou un filtre inconnu terminera l'appel.",
        syntax: 'Syntaxe',
        namespaces: 'Namespaces disponibles',
        availableVariables: 'Variables disponibles',
        namespaceCall:
          'account, account_user, account_domain, called_account, caller_id_name, caller_id_number, called_id_name, called_id_number, from_host, to_host, remote_network_addr',
        namespaceCustomVars: 'Variables personnalisées définies plus tôt dans le flux (ex. par un nœud mapping)',
        namespaceSipHeaders: "Valeurs des en-têtes SIP (lecture seule, les noms d'en-tête peuvent contenir des tirets)",
        namespaceRoot: 'uuid (UUID du canal), realm',
        namespaceHelpers: 'date (horodatage UTC au format YYYY-MM-DD-HH-MM-SS)',
        filters: 'Filtres (chaîner avec |)',
        filterUserPart: 'Partie avant le dernier @',
        filterDomainPart: 'Partie après le dernier @',
        filterUriEncode: 'Encodage pourcentage pour une utilisation sûre dans les URIs',
        filterOrempty: 'Chaîne vide si non défini (doit être le premier dans la chaîne)',
        examples: 'Exemples',
      },
    },
    nodes: {
      answer: {
        name: 'Réponse',
        delay: 'Délai',
        delayDescription:
          'Millisecondes à attendre avant de continuer, utile pour donner le temps au chemin audio de se stabiliser',
        answerShouldHaveConnection: 'Le noeud de réponse doit avoir une connexion',
      },
      calendar: {
        name: 'Calendrier',
        calendarIsNotSelected: 'Le calendrier n est pas sélectionné',
        calendarHasNoTarget: 'Le calendrier n a pas de cible',
      },
      call_me: {
        name: 'Appeler',
      },
      conference: {
        name: 'Conférence',
        conferenceErrorEmpty: 'La conférence ne peut pas être vide',
      },
      disconnect: {
        name: 'Déconnecter',
        reason: 'Raison',
        reasonNormal: 'Normal',
        reasonNotFound: 'Non trouvé',
      },
      distribution_list: {
        name: 'Liste de distribution',
        distributionListErrorEmpty: 'La liste de distribution ne peut pas être vide',
        distributionListHasNoTarget: 'La liste de distribution n a pas de cible',
      },
      extension: {
        name: 'Extension',
        connectionShouldHaveMatchExitCause: 'La connexion de l extension doit avoir une cause de sortie correspondante',
        connectionShouldHaveTarget: 'La connexion de l extension doit avoir une cible',
      },
      forward: {
        name: 'Transfert',
        number: 'Numéro',
        forwardErrorEmpty: "Le numéro de transfert n'est pas défini",
      },
      goto: {
        name: 'Goto',
        gotoErrorEmpty: 'La cible Goto n est pas définie!',
      },
      groupNode: {
        name: 'Groupe',
        groupErrorEmpty: 'Le groupe ne peut pas être vide',
        callTimeout: "Délai d'appel",
      },
      http: {
        name: 'HTTP',
        urlDescription: "L'URL externe à appeler",
        urlErrorEmpty: "L'URL ne peut pas être vide",
        urlErrorInvalid: "L'URL doit être valide et commencer par http:// ou https://",
        method: 'Méthode',
        methodDescription: 'Méthode HTTP à utiliser pour la requête',
        methodPost: 'POST',
        methodGet: 'GET',
        headers: 'Headers',
        headersTooltip: `Une requête HTTP externe qui doit renvoyer un autre Callflow. Lorsque l'exécution du callflow atteint un nœud CallflowHTTP,
        une requête sera faite à l'URL spécifiée; si la méthode est POST, l'état d'appel sera envoyé en tant que requête
        charge utile. Le serveur contacté doit répondre avec un callflow, sous la forme d'un objet JSON contenant l'entrée
        et les champs de nœuds. Le callflow retourné ne doit pas contenir de boucles, c'est-à-dire qu'il ne peut pas s'agir d'un graphe arbitraire mais
        doit être un arbre; en outre, tous les nœuds ne sont pas pris en charge, mais seulement ceux avec le
        x-voismart-simple-callflow-node propriété. Si l'une de ces conditions requises pour le Callflow retourné est
        violé, l'appel sera abandonné.`,
        addHeader: 'Ajouter un header',
        headerName: "Nom de l'en-tête",
        headerNameErrorEmpty: "Le nom de l'en-tête ne peut pas être vide",
        headerNameErrorInvalid: 'Le nom doit être valide et commencer par x- ou être "autorization"',
        headerValue: "Valeur de l'en-tête",
        headerValueErrorEmpty: "La valeur de l'en-tête ne peut pas être vide",
        headersError: 'Les en-têtes sont incomplets',
        sslVerify: 'Vérification SSL',
      },
      hunt: {
        name: 'Hunting',
        enableScreening: 'Activer le screening',
        huntingGroup: 'Hunting group',
        parallel: 'En parallele',
        huntingGroupErrorEmpty: 'Le hunting group ne peut pas être vide',
      },
      is_from_queue: {
        name: "Appel en file d'attente ?",
        isFromQueueHasNoTarget: '"Appel en file d\'attente ?" n a pas de cible',
      },
      is_external: {
        name: 'Appel externe ?',
        isExternalHasNoTarget: '"Appel externe" n a pas de cible',
      },
      ivr: {
        name: 'IVR',
        errorSoundfile: 'Fichier audio d erreur',
        menuSoundfile: 'Fichier audio du menu',
        retries: 'Nombre de tentatives',
        selections: 'Sélections',
        selection: 'Sélection',
        timeout: 'Délai d attente',
        timeoutSoundfile: 'Fichier audio de délai d attente',
        selectionsErrorEmpty: 'Les sélections ne peuvent pas être vides',
        selectionShouldHaveConnection: 'Les sélections IVR doivent être connectées',
        soundfileErrorEmpty: 'Le fichier audio ne peut pas être vide',
      },
      lcr: {
        name: 'LCR',
        userIsNotSelected: "L'utilisateur n'est pas sélectionné",
      },
      mapping: {
        name: 'Mapping',
        setProperty: 'Définir la propriété',
        customSetProperty: 'Définir la propriété personnalisée',
        useCustomSetProperty: 'Utiliser une propriété personnalisée',
        addMappingRule: 'Ajouter une règle de correspondance',
        replaceRule: 'Remplacer la règle',
        mappingRuleErrorEmpty: 'La règle de correspondance ne peut pas être vide',
        mappingHasNoTarget: 'Le mapping n a pas de cible',
        mapTo: '%{matchRule} vers %{replaceRule}',
      },
      playback: {
        name: 'Lecture',
        audioSourceNotSelected: "La source audio n'est pas sélectionnée",
        useUrlSource: 'Utiliser une source URL',
        useDTMFGathering: 'Utiliser la collecte DTMF',
        maxDigits: 'Max. Chiffres',
        maxDigitsErrorInvalid: 'Le nombre maximum de chiffres doit être compris entre 1 et 128.',
        outputPropName: 'Nom de la propriété de sortie',
        outputPropNameErrorEmpty: 'Le nom de la propriété de sortie ne peut pas être vide',
        outputPropNameErrorInvalid: 'Le nom de la propriété de sortie doit être valide',
        soundfile: 'Fichier audio',
        terminators: 'Terminators',
        terminatorsErrorInvalid: 'Les terminateurs doivent être valides et ne contenir que des chiffres, # et *.',
        timeout: 'Délai d attente',
        timeoutErrorInvalid: 'Le délai doit être compris entre 500 et 60000.',
      },
      presence_router: {
        name: 'Routeur de presence',
        availableConnection: 'Connexion disponible',
        awayConnection: 'Connexion absente',
        presenceRouterShouldHaveConnection: 'Le routeur de presence doit avoir une connexion',
      },
      queue: {
        name: 'File d attente',
        queueErrorEmpty: 'La file d attente ne peut pas être vide',
        connectionShouldHaveMatchExitCause: 'File d attente doit avoir une cause de sortie correspondante',
        connectionShouldHaveTarget: 'File d attente doit avoir une cible',
      },
      response_router: {
        name: 'Routeur de résponse',
        busyConnection: 'Connexion en conflit',
        timeoutConnection: 'Connexion en démarrage',
        unreachableConnection: 'Connexion non accessible',
        responseRouterShouldHaveConnection: 'Le routeur de réponse doit avoir une connexion',
      },
      router: {
        name: 'Routeur',
        matchProperty: 'Propriété de correspondance',
        customMatchProperty: 'Propriété de correspondance personnalisée',
        useCustomMatchProperty: 'Utiliser une propriété de correspondance personnalisée',
        callPropertyErrorEmpty: '%{label} ne peut pas être vide',
        callPropertyErrorInvalid:
          "%{label} doit être composé d'au moins 3 symboles, commencer par une lettre et ne contenir que des lettres et des chiffres",
        matchRules: 'Règles de correspondance',
        matchRule: 'Règle de correspondance',
        matchRuleErrorEmpty: 'La règle de correspondance ne peut pas être vide',
        matchRuleShouldHaveConnection: 'La règle de correspondance du routeur doit avoir une connexion',
      },
      speech: {
        name: 'Parole',
        auth_key: "Clé d'authentification",
        authKeyErrorEmpty: "La clé d'authentification ne peut pas être vide",
        encoding: 'Encodage',
        encodingWav: 'WAV',
        encodingMp3: 'MP3',
        language: 'Langue',
        languageIt: 'Italien',
        languageEn: 'Anglais',
        text: 'Texte',
        textErrorEmpty: 'Le texte ne peut pas être vide',
      },
      user: {
        name: 'Utilisateur',
        userIsNotSelected: "L'utilisateur n'est pas sélectionné",
        connectionShouldHaveMatchExitCause: 'L utilisateur doit avoir une cause de sortie correspondante',
        connectionShouldHaveTarget: 'L utilisateur doit avoir une cible',
      },
      personal_voicemail: {
        name: 'Messagerie vocale',
      },
      user_voicemail: {
        name: 'Messagerie vocale',
        userIsNotSelected: "L'utilisateur n'est pas sélectionné",
      },
      ai_assistant: {
        name: 'Assistant AI',
        instructions: 'Instructions',
        instructionsErrorEmpty: 'Les instructions ne peuvent pas être vides',
        model: 'Modèle',
        voice: 'Voix',
        defaultInstructions:
          "Votre travail consiste à répondre aux appels téléphoniques dirigés vers une entreprise. Vous devez accueillir l'appelant et lui demander comment vous pouvez l'aider. Vous devez utiliser vos outils pour diriger l'appel vers la destination la plus appropriée selon la demande de l'appelant. Vous devez être poli et professionnel. Vous ne devez pas permettre à l'utilisateur d'orienter la conversation vers des sujets sans rapport avec l'entreprise.",
        transcription: {
          enabled: 'Activer la transcription',
          model: 'Modèle de transcription',
        },
      },
      ai_voice_analysis: {
        name: 'Analyse vocale IA',
        titleStart: 'Démarrer la vérification de voix falsifiée',
        titleStop: 'Arrêter la vérification de voix falsifiée',
        action: 'Action',
        actionStart: 'Démarrer l’analyse',
        actionStop: 'Arrêter l’analyse',
        actionDescription:
          'Démarre, ou arrête, l’analyse IA de la voix de l’interlocuteur distant. Le jeton API Realynx doit être configuré sur le domaine, sinon le noeud n’a aucun effet.',
        aiVoiceAnalysisShouldHaveConnection: 'Le noeud d’analyse vocale IA doit avoir une connexion',
      },
      label: 'Étiquette',
    },
    notifications: {
      saved: 'Dialplan enregistré avec succès',
      updated: 'Dialplan mis à jour avec succès',
      restored: 'La sauvegarde du Dialplan a été restaurée avec succès',
      errorSaving: "Erreur lors de l'enregistrement du dialplan. Veuillez vérifier votre dialplan et réessayer.",
      callflowDataError: "Les données du flux d'appels ne sont pas valides. %{error}",
      deleteConfirmation: 'Êtes-vous sûr de vouloir supprimer ce dialplan?',
      deleteConfirmationTitle: 'Supprimer %{title} ?',
      nodeIsNotConnectedToMainFlow: 'Ce noeud n est connecté à aucun flux principal.',
      errorSorting:
        'Erreur de tri des nœuds. Veuillez vérifier que les nœuds sont correctement connectés et réessayer.',
      errorSortingMultipleRoots: 'Erreur de tri des nœuds. Tous les nœuds ne sont pas connectés au flux principal.',
    },
  },
};
